import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: white;
`;

const Navigation = styled.div`
  width: 280px;
  background: #f8f9fa;
  border-right: 1px solid #e5e7eb;
  padding: 24px;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
`;

const NavTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 24px;
  color: #1a1a1a;
  font-weight: 600;
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NavDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: none;
  background: ${props => props.isActive ? '#e5e7eb' : 'transparent'};
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.isActive ? '#e5e7eb' : '#f1f3f5'};
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const ContentArea = styled.div`
  margin-left: 280px;
  flex: 1;
  padding: 32px;
  max-width: calc(100vw - 280px);
`;

const PreviewArea = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 36px;
  margin-bottom: 24px;
  color: #1a1a1a;
  font-weight: 600;
  text-align: center;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 48px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PreviewCard = styled.div`
  width: ${props => 
    props.platform === 'linkedin' ? '1200px' :
    props.platform === 'x' ? '1024px' :
    props.platform === 'facebook' ? '1200px' :
    props.platform === 'google' ? '800px' : '1200px'
  };
  background: ${props => 
    props.platform === 'linkedin' ? '#f3f2ef' :
    props.platform === 'x' ? '#ffffff' :
    props.platform === 'facebook' ? '#ffffff' :
    props.platform === 'google' ? '#ffffff' : '#ffffff'
  };
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
`;

const PreviewHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlatformIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const PlatformName = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const PreviewContent = styled.div`
  padding: ${props => props.platform === 'google' ? '20px' : '0'};
`;

const PreviewImage = styled.img`
    width: 100%;
  height: ${props =>
    props.platform === 'x' ? '512px' :
    props.platform === 'linkedin' ? '627px' :
    props.platform === 'facebook' ? '630px' : '630px'
  };
    object-fit: cover;
`;

const PreviewTitle = styled.h3`
  font-size: ${props =>
    props.platform === 'linkedin' ? '16px' :
    props.platform === 'x' ? '15px' :
    props.platform === 'facebook' ? '16px' :
    props.platform === 'google' ? '20px' : '16px'
  };
  font-weight: 600;
  margin: 12px 16px;
  color: ${props => props.platform === 'google' ? '#1a0dab' : '#1a1a1a'};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${props =>
    props.platform === 'linkedin' ? '2' :
    props.platform === 'x' ? '1' :
    props.platform === 'facebook' ? '2' :
    props.platform === 'google' ? '1' : '2'
  };
  -webkit-box-orient: vertical;
  text-decoration: ${props => props.platform === 'google' ? 'none' : 'initial'};
  &:hover {
    text-decoration: ${props => props.platform === 'google' ? 'underline' : 'initial'};
  }
`;

const PreviewDescription = styled.p`
  font-size: 14px;
  color: ${props => props.platform === 'google' ? '#545454' : '#4b5563'};
  margin: 8px 16px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${props =>
    props.platform === 'linkedin' ? '3' :
    props.platform === 'x' ? '2' :
    props.platform === 'facebook' ? '3' :
    props.platform === 'google' ? '2' : '3'
  };
  -webkit-box-orient: vertical;
`;

const PreviewUrl = styled.div`
  font-size: 12px;
  color: ${props => props.platform === 'google' ? '#006621' : '#6b7280'};
  margin: 8px 16px;
  text-transform: ${props => props.platform === 'facebook' ? 'uppercase' : 'none'};
`;

const MetaTagsContainer = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  margin: 0 auto 48px;
  max-width: 1000px;
  overflow: hidden;
`;

const MetaTagsHeader = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const MetaTagsTitle = styled.h2`
  color: #ffffff;
  font-size: 18px;
  margin: 0;
`;

const ChevronIcon = styled(ChevronDown)`
  color: #ffffff;
    transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const MetaTagsContent = styled.div`
  height: ${props => props.isOpen ? 'auto' : '0'};
  overflow: hidden;
  transition: height 0.3s ease;
`;

const MetaTagsCode = styled.pre`
  color: #e5e7eb;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  padding: ${props => props.isOpen ? '0 24px 24px' : '0 24px'};
  
  .tag { color: #9ecbff; }
  .attribute { color: #79c0ff; }
  .value { color: #a5d6ff; }
`;

const platforms = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    titleLimit: 70,
    descriptionLimit: 156,
    imageWidth: 1200,
    imageHeight: 627,
    icon: '/icons/linkedin.svg'
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    titleLimit: 70,
    descriptionLimit: 200,
    imageWidth: 1024,
    imageHeight: 512,
    icon: '/icons/twitter.svg'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    titleLimit: 100,
    descriptionLimit: 200,
    imageWidth: 1200,
    imageHeight: 630,
    icon: '/icons/facebook.svg'
  },
  {
    id: 'google',
    name: 'Google',
    titleLimit: 60,
    descriptionLimit: 160,
    imageWidth: 800,
    imageHeight: 0,
    icon: '/icons/google.svg'
  }
];

const FacebookPreviewCard = styled.div`
  width: 527px;
  background: white;
  border: 1px solid #dadde1;
  border-radius: 0;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const FacebookPreviewImage = styled.img`
  width: 527px;
  height: 276px;
  object-fit: cover;
  display: block;
`;

const FacebookPreviewContent = styled.div`
  padding: 10px 12px;
  background: #F2F3F5;
`;

const FacebookDomain = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: #606770;
  text-transform: uppercase;
  font-weight: 400;
  margin-bottom: 4px;
  letter-spacing: 0.2px;
`;

const FacebookTitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #1d2129;
  font-weight: 600;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const FacebookDescription = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: #606770;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const XPreviewCard = styled.div`
  width: 527px;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  border: 1px solid rgb(207, 217, 222);
  position: relative;
`;

const XPreviewImage = styled.img`
  width: 100%;
  height: 276px;
  object-fit: cover;
  display: block;
`;

const XPreviewTitleOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
`;

const LinkedInPreviewCard = styled.div`
  width: 552px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.05);
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const LinkedInPreviewImage = styled.img`
  width: 100%;
  height: 288px;
  object-fit: cover;
  display: block;
`;

const LinkedInPreviewContent = styled.div`
  padding: 16px 16px 20px;
  background: white;
`;

const LinkedInTitle = styled.div`
  font-size: 20px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.9);
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: -0.2px;
`;

const LinkedInDomain = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 400;
`;

const DiscordPreviewCard = styled.div`
  width: 520px;
  background: #2b2d31;
  border-radius: 4px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  padding: 16px;
`;

const DiscordContent = styled.div`
  margin-bottom: 16px;
`;

const DiscordDomain = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #dbdee1;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const DiscordTitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #00a8fc;
  font-weight: 600;
  margin-bottom: 8px;
`;

const DiscordDescription = styled.div`
  font-size: 14px;
  line-height: 18px;
  color: #dbdee1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const DiscordPreviewImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
  border-radius: 4px;
`;

const PinterestPreviewCard = styled.div`
  width: 400px;
  background: white;
  border-radius: 32px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const PinterestPreviewImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  display: block;
`;

const PinterestPreviewContent = styled.div`
  padding: 16px;
`;

const PinterestTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const PinterestDescription = styled.div`
  font-size: 14px;
  color: #767676;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const PinterestDomain = styled.div`
  font-size: 14px;
  color: #111;
  font-weight: 500;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  img {
    width: 12px;
    height: 12px;
    opacity: 0.6;
  }
`;

const PinterestMetaInfo = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 12px;
  color: #767676;
`;

const PinterestErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const PinterestErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #5f5f5f;
  font-size: 32px;
  font-weight: 600;
`;

const PinterestErrorTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
`;

const PinterestErrorMessage = styled.div`
  font-size: 18px;
  color: #767676;
  line-height: 1.4;
`;

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
`;

const ComparisonCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const ComparisonHeader = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
    color: #4b5563;
  }
`;

const ComparisonContent = styled.div`
  padding: 16px;
`;

const MetaRow = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MetaLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const MetaValue = styled.div`
  font-size: 14px;
  color: #1a1a1a;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DiffIndicator = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 8px;
  background: ${props => props.isDifferent ? '#ef4444' : '#22c55e'};
`;

const SocialCardPage = () => {
  const [metadata, setMetadata] = useState({
    title: '',
    description: '',
    url: '',
    image: '',
    siteName: '',
    type: '',
    articleMeta: {
      publishedTime: '',
      modifiedTime: '',
      author: '',
      section: '',
      tags: []
    }
  });
  const [isMetaTagsOpen, setIsMetaTagsOpen] = useState(false);
  const [activePreview, setActivePreview] = useState('pinterest');

  const [componentMetadata] = useState({
    title: "AI Employee | Intelligent Workplace Assistant",
    description: "Transform your business with AI-powered digital workforce. Automate tasks, streamline workflows, and boost productivity with our intelligent digital workers.",
    image: "https://cdn.nexastaff.ai/back.jpg",
    url: "https://nexastaff.ai",
    type: "article",
    siteName: "NEXASTAFF.AI",
    articleMeta: {
      publishedTime: new Date().toISOString(),
      modifiedTime: new Date().toISOString(),
      author: "NEXASTAFF.AI",
      section: "Technology",
      tags: ["AI", "Digital Workforce", "Automation"]
    }
  });

  useEffect(() => {
    // Read meta tags directly from the document
    const metaTags = {
      title: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || document.title,
      description: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      url: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || window.location.origin,
      image: document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
      siteName: document.querySelector('meta[property="og:site_name"]')?.getAttribute('content') || '',
      type: document.querySelector('meta[property="og:type"]')?.getAttribute('content') || '',
      articleMeta: {
        publishedTime: document.querySelector('meta[property="article:published_time"]')?.getAttribute('content') || '',
        modifiedTime: document.querySelector('meta[property="article:modified_time"]')?.getAttribute('content') || '',
        author: document.querySelector('meta[property="article:author"]')?.getAttribute('content') || '',
        section: document.querySelector('meta[property="article:section"]')?.getAttribute('content') || '',
        tags: Array.from(document.querySelectorAll('meta[property="article:tag"]')).map(tag => tag.getAttribute('content'))
      }
    };
    setMetadata(metaTags);
  }, []);

  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname;
    } catch (e) {
      return url;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return '';
    }
  };

  const hasRichPinData = () => {
    return metadata.type === 'article' && 
           metadata.articleMeta.publishedTime && 
           metadata.articleMeta.author &&
           metadata.title &&
           metadata.description &&
           metadata.image;
  };

  const socialPlatforms = [
    { id: 'pinterest', name: 'Pinterest', icon: '/icons/pinterest.svg' },
    { id: 'discord', name: 'Discord', icon: '/icons/discord.svg' },
    { id: 'linkedin', name: 'LinkedIn', icon: '/icons/linkedin.svg' },
    { id: 'x', name: 'X (Twitter)', icon: '/icons/twitter.svg' },
    { id: 'facebook', name: 'Facebook', icon: '/icons/facebook.svg' },
    { id: 'google', name: 'Google', icon: '/icons/google.svg' }
  ];

  const renderPreview = (platform) => {
    switch (platform) {
      case 'pinterest':
        return (
          <div>
            <PinterestPreviewCard>
              {hasRichPinData() ? (
                <>
                  <PinterestPreviewImage src={metadata.image} alt={metadata.title} />
                  <PinterestPreviewContent>
                    <PinterestTitle>{metadata.title}</PinterestTitle>
                    <PinterestDescription>{metadata.description}</PinterestDescription>
                    <PinterestDomain>
                      <img src="/icons/pinterest.svg" alt="" />
                      {getDomainFromUrl(metadata.url)}
                    </PinterestDomain>
                    <PinterestMetaInfo>
                      {metadata.articleMeta.author && <div>By {metadata.articleMeta.author}</div>}
                      {metadata.articleMeta.publishedTime && (
                        <div>Published {formatDate(metadata.articleMeta.publishedTime)}</div>
                      )}
                      {metadata.articleMeta.section && <div>Section: {metadata.articleMeta.section}</div>}
                    </PinterestMetaInfo>
                  </PinterestPreviewContent>
                </>
              ) : (
                <PinterestErrorContent>
                  <PinterestErrorIcon>!</PinterestErrorIcon>
                  <PinterestErrorTitle>Pinterest Rich Pin Check</PinterestErrorTitle>
                  <PinterestErrorMessage>
                    {!metadata.type ? "Missing og:type meta tag" :
                     !metadata.articleMeta.publishedTime ? "Missing article:published_time meta tag" :
                     !metadata.articleMeta.author ? "Missing article:author meta tag" :
                     !metadata.title ? "Missing og:title meta tag" :
                     !metadata.description ? "Missing og:description meta tag" :
                     !metadata.image ? "Missing og:image meta tag" :
                     "Missing required Rich Pin metadata"}
                  </PinterestErrorMessage>
                </PinterestErrorContent>
              )}
            </PinterestPreviewCard>
          </div>
        );
      case 'discord':
        return (
          <DiscordPreviewCard>
            <DiscordContent>
              <DiscordDomain>{getDomainFromUrl(metadata.url)}</DiscordDomain>
              <DiscordTitle>{metadata.title}</DiscordTitle>
              <DiscordDescription>{metadata.description}</DiscordDescription>
            </DiscordContent>
            <DiscordPreviewImage src={metadata.image} alt={metadata.title} />
          </DiscordPreviewCard>
        );
      case 'linkedin':
        return (
          <LinkedInPreviewCard>
            <LinkedInPreviewImage src={metadata.image} alt={metadata.title} />
            <LinkedInPreviewContent>
              <LinkedInTitle>{metadata.title}</LinkedInTitle>
              <LinkedInDomain>{getDomainFromUrl(metadata.url)}</LinkedInDomain>
            </LinkedInPreviewContent>
          </LinkedInPreviewCard>
        );
      case 'x':
        return (
          <XPreviewCard>
            <XPreviewImage src={metadata.image} alt={metadata.title} />
            <XPreviewTitleOverlay>{metadata.title}</XPreviewTitleOverlay>
          </XPreviewCard>
        );
      case 'facebook':
        return (
          <FacebookPreviewCard>
            <FacebookPreviewImage src={metadata.image} alt={metadata.title} />
            <FacebookPreviewContent>
              <FacebookDomain>{getDomainFromUrl(metadata.url)}</FacebookDomain>
              <FacebookTitle>{metadata.title}</FacebookTitle>
              <FacebookDescription>{metadata.description}</FacebookDescription>
            </FacebookPreviewContent>
          </FacebookPreviewCard>
        );
      case 'google':
        return (
          <PreviewCard platform="google">
            <PreviewContent platform="google">
              <PreviewTitle platform="google">{metadata.title}</PreviewTitle>
              <PreviewUrl platform="google">{getDomainFromUrl(metadata.url)}</PreviewUrl>
              <PreviewDescription platform="google">{metadata.description}</PreviewDescription>
            </PreviewContent>
          </PreviewCard>
        );
      default:
        return null;
    }
  };

  const renderComparison = () => {
    const compareValues = (htmlValue, componentValue) => {
      return htmlValue === componentValue;
    };

    return (
      <ComparisonContainer>
        <ComparisonCard>
          <ComparisonHeader>
            <span style={{ width: 20, textAlign: 'center' }}>&lt;/&gt;</span>
            HTML Meta Tags
          </ComparisonHeader>
          <ComparisonContent>
            <MetaRow>
              <MetaLabel>og:title</MetaLabel>
              <MetaValue>{metadata.title}<DiffIndicator isDifferent={!compareValues(metadata.title, componentMetadata.title)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>og:description</MetaLabel>
              <MetaValue>{metadata.description}<DiffIndicator isDifferent={!compareValues(metadata.description, componentMetadata.description)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>og:image</MetaLabel>
              <MetaValue>{metadata.image}<DiffIndicator isDifferent={!compareValues(metadata.image, componentMetadata.image)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>og:url</MetaLabel>
              <MetaValue>{metadata.url}<DiffIndicator isDifferent={!compareValues(metadata.url, componentMetadata.url)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>og:type</MetaLabel>
              <MetaValue>{metadata.type}<DiffIndicator isDifferent={!compareValues(metadata.type, componentMetadata.type)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>article:author</MetaLabel>
              <MetaValue>{metadata.articleMeta.author}<DiffIndicator isDifferent={!compareValues(metadata.articleMeta.author, componentMetadata.articleMeta.author)} /></MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>article:section</MetaLabel>
              <MetaValue>{metadata.articleMeta.section}<DiffIndicator isDifferent={!compareValues(metadata.articleMeta.section, componentMetadata.articleMeta.section)} /></MetaValue>
            </MetaRow>
          </ComparisonContent>
        </ComparisonCard>

        <ComparisonCard>
          <ComparisonHeader>
            <span style={{ width: 20, textAlign: 'center' }}>⚛️</span>
            React Component Default Values
          </ComparisonHeader>
          <ComparisonContent>
            <MetaRow>
              <MetaLabel>title</MetaLabel>
              <MetaValue>{componentMetadata.title}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>description</MetaLabel>
              <MetaValue>{componentMetadata.description}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>image</MetaLabel>
              <MetaValue>{componentMetadata.image}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>url</MetaLabel>
              <MetaValue>{componentMetadata.url}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>type</MetaLabel>
              <MetaValue>{componentMetadata.type}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>author</MetaLabel>
              <MetaValue>{componentMetadata.articleMeta.author}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>section</MetaLabel>
              <MetaValue>{componentMetadata.articleMeta.section}</MetaValue>
            </MetaRow>
          </ComparisonContent>
        </ComparisonCard>
      </ComparisonContainer>
    );
  };

  return (
    <PageContainer>
      <Navigation>
        <NavTitle>Social Preview</NavTitle>
        <NavList>
          {socialPlatforms.map(platform => (
            <NavItem
              key={platform.id}
              isActive={activePreview === platform.id}
              onClick={() => setActivePreview(platform.id)}
            >
              <img src={platform.icon} alt={platform.name} />
              {platform.name}
            </NavItem>
          ))}
          <NavDivider />
          <NavItem
            isActive={activePreview === 'meta-tags'}
            onClick={() => setActivePreview('meta-tags')}
          >
            <span style={{ width: 20, textAlign: 'center' }}>&lt;/&gt;</span>
            Meta Tags
          </NavItem>
          <NavItem
            isActive={activePreview === 'comparison'}
            onClick={() => setActivePreview('comparison')}
          >
            <span style={{ width: 20, textAlign: 'center' }}>⚖️</span>
            Compare Sources
          </NavItem>
        </NavList>
      </Navigation>
      
      <ContentArea>
        <PreviewArea>
          {activePreview === 'meta-tags' ? (
            <MetaTagsContainer>
              <MetaTagsCode>
                {`<!-- Primary Meta Tags -->
<meta name="title" content="${metadata.title}" />
<meta name="description" content="${metadata.description}" />

<!-- Open Graph / Facebook -->
<meta property="og:type" content="website" />
<meta property="og:url" content="${metadata.url}" />
<meta property="og:title" content="${metadata.title}" />
<meta property="og:description" content="${metadata.description}" />
<meta property="og:image" content="${metadata.image}" />
<meta property="og:site_name" content="${metadata.siteName}" />

<!-- Twitter -->
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="${metadata.title}" />
<meta name="twitter:description" content="${metadata.description}" />
<meta name="twitter:image" content="${metadata.image}" />`}
              </MetaTagsCode>
            </MetaTagsContainer>
          ) : activePreview === 'comparison' ? (
            renderComparison()
          ) : (
            renderPreview(activePreview)
          )}
        </PreviewArea>
      </ContentArea>
    </PageContainer>
  );
};

export default SocialCardPage; 